/**
 * ROOF ROUND E2 — the curated roof-framing data family (docs/MODELER.md "ROOF ROUND
 * E2"): nominal lumber sizes, rafter spacings, and the TAIL PROFILE family. Pure data
 * + lookups, no geometry — bim-geometry's framing generator and the roof schema's
 * choice lists both read from HERE so the curated set has exactly one home.
 *
 * TAIL PROFILES: each profile is the member's BOTTOM cut, as a normalized polyline —
 * `u` 0→1 from the cut start to the tip, `frac` = remaining member depth (1 = full).
 * Since E4 r2 the cut spans THE PROFILE RUN (2× the member depth, capped by the
 * available run — bim-geometry framing.ts), not the whole exposed overhang, so the
 * shape is intrinsic to the member and tails/barges match in elevation.
 * A repeated `u` makes a plumb face
 * (plumb+seat's vertical riser). The family is code-curated v1 — a new profile is a
 * new entry here (they're formulas by nature); promoting the family to a data-driven
 * firm-library record kind (curator-drawn polylines) is the recorded follow-on. The
 * FIRM STANDARD — which profile each roof type uses — already lives in the firm
 * library, riding the roof TYPE's `tailStyle` param.
 */

export interface LumberSize {
  key: string
  label: string
  widthIn: number
  depthIn: number
}

/** field rafter sizes (E2 curated set — 2×6…2×12) */
export const RAFTER_SIZES: LumberSize[] = [
  { key: '2x6', label: '2×6', widthIn: 1.5, depthIn: 5.5 },
  { key: '2x8', label: '2×8', widthIn: 1.5, depthIn: 7.25 },
  { key: '2x10', label: '2×10', widthIn: 1.5, depthIn: 9.25 },
  { key: '2x12', label: '2×12', widthIn: 1.5, depthIn: 11.25 },
]

/** decorative tail sizes — the SHOW members, larger stock at wider spacing */
export const TAIL_SIZES: LumberSize[] = [
  { key: '2x8', label: '2×8', widthIn: 1.5, depthIn: 7.25 },
  { key: '2x10', label: '2×10', widthIn: 1.5, depthIn: 9.25 },
  { key: '3x6', label: '3×6', widthIn: 2.5, depthIn: 5.5 },
  { key: '3x8', label: '3×8', widthIn: 2.5, depthIn: 7.25 },
  { key: '4x6', label: '4×6', widthIn: 3.5, depthIn: 5.5 },
  { key: '4x8', label: '4×8', widthIn: 3.5, depthIn: 7.25 },
  { key: '4x10', label: '4×10', widthIn: 3.5, depthIn: 9.25 },
  { key: '4x12', label: '4×12', widthIn: 3.5, depthIn: 11.25 },
]

/** rafter spacings, inches on center (ROOF CORE DEFINITION round, 2026-08-18:
 *  unified with the floor list — 19.2 dropped for 12/18/20) */
export const RAFTER_SPACINGS = [12, 16, 18, 20, 24] as const

/** fascia board sizes (E3 curated set — 1× trim and 2× sub-fascia stock) */
export const FASCIA_SIZES: LumberSize[] = [
  { key: '1x6', label: '1×6', widthIn: 0.75, depthIn: 5.5 },
  { key: '1x8', label: '1×8', widthIn: 0.75, depthIn: 7.25 },
  { key: '2x6', label: '2×6', widthIn: 1.5, depthIn: 5.5 },
  { key: '2x8', label: '2×8', widthIn: 1.5, depthIn: 7.25 },
  { key: '2x10', label: '2×10', widthIn: 1.5, depthIn: 9.25 },
]

/** FRIEZE BOARD (2026-10-07, Matt): the trim board on the wall face between the rafter
 *  tails (notched around them) or behind the corbels, under the decking / soffit */
export const FRIEZE_SIZES: LumberSize[] = [
  { key: '1x4', label: '1×4', widthIn: 0.75, depthIn: 3.5 },
  { key: '1x6', label: '1×6', widthIn: 0.75, depthIn: 5.5 },
  { key: '1x8', label: '1×8', widthIn: 0.75, depthIn: 7.25 },
  { key: '1x10', label: '1×10', widthIn: 0.75, depthIn: 9.25 },
  { key: '2x4', label: '2×4', widthIn: 1.5, depthIn: 3.5 },
  { key: '2x6', label: '2×6', widthIn: 1.5, depthIn: 5.5 },
  { key: '2x8', label: '2×8', widthIn: 1.5, depthIn: 7.25 },
]

/** FLAT SOFFIT FRAMING (2026-10-07): the ledger on the wall sheathing + the lookouts out
 *  to the fascia under a flat soffit — light stock, on edge */
export const SOFFIT_FRAMING_SIZES: LumberSize[] = [
  { key: '2x4', label: '2×4', widthIn: 1.5, depthIn: 3.5 },
  { key: '2x6', label: '2×6', widthIn: 1.5, depthIn: 5.5 },
]

/**
 * Gutter profiles (E3): HALF-ROUND / BOX / TAPERED only. K-style is DELIBERATELY
 * absent from this library — enforcement by omission, per the firm's standards.
 * Do not add it.
 */
export const GUTTER_PROFILES = [
  { key: 'halfRound', label: 'Half-Round' },
  { key: 'box', label: 'Box' },
  { key: 'tapered', label: 'Tapered' },
] as const

export interface GutterSize { key: string; label: string; widthIn: number; heightIn: number }
export const GUTTER_SIZES: GutterSize[] = [
  { key: '5', label: '5″', widthIn: 5, heightIn: 4 },
  { key: '6', label: '6″', widthIn: 6, heightIn: 5 },
]

export interface TailProfile {
  key: string
  label: string
  /** bottom cut over the exposed run: [u 0→1 wall→tip, remaining-depth fraction] */
  bottom: [number, number][]
}

/**
 * THE END-STYLE FAMILY (E4 r3, Matt, 2026-08-11 — replacing the original three-
 * profile set with the firm's 15-profile reference chart, one family shared by
 * rafter TAILS, OUTLOOKER bracket ends, and the BARGE's lower cut). Keys s1–s15
 * match the chart's numbering; 'straight' — a plain full-depth plumb cut, no
 * style — is the DEFAULT and sits first. Curves are polyline approximations of
 * the chart silhouettes; a repeated `u` makes a plumb (vertical) face. Legacy
 * keys ('taper'/'ogee'/'plumbSeat'/'square') resolve via LEGACY_PROFILE_ALIASES
 * below — never re-add them here.
 */
export const TAIL_PROFILES: TailProfile[] = [
  { key: 'straight', label: 'Straight Cut', bottom: [[0, 1], [1, 1]] },
  {
    key: 's1', label: '1 · Cyma & Bead',
    // small bead notch at the start, then the reverse curve sweeping to a thin tip
    bottom: [[0, 1], [0.05, 0.86], [0.11, 0.94], [0.18, 0.88], [0.32, 0.72], [0.5, 0.5], [0.68, 0.3], [0.85, 0.14], [1, 0.06]],
  },
  {
    key: 's2', label: '2 · Cove & Nose',
    bottom: [[0, 1], [0.15, 0.96], [0.38, 0.82], [0.6, 0.58], [0.78, 0.32], [0.86, 0.24], [1, 0.24]],
  },
  {
    key: 's3', label: '3 · Cove',
    bottom: [[0, 1], [0.22, 0.95], [0.45, 0.82], [0.68, 0.58], [0.86, 0.3], [1, 0.05]],
  },
  {
    key: 's4', label: '4 · Taper',
    // (2026-10-05, Matt: "We don't want a nose in a taper profile") — the taper runs
    // all the way to the tip; it used to hold flat at 36% for the last 12%
    bottom: [[0, 1], [1, 0.36]],
  },
  {
    key: 's5', label: '5 · Steps & Cove',
    bottom: [[0, 1], [0.1, 1], [0.1, 0.8], [0.3, 0.76], [0.5, 0.58], [0.66, 0.38], [0.74, 0.32], [0.74, 0.2], [1, 0.2]],
  },
  {
    key: 's6', label: '6 · Cyma',
    bottom: [[0, 1], [0.16, 0.92], [0.34, 0.76], [0.52, 0.52], [0.7, 0.28], [0.88, 0.1], [1, 0.05]],
  },
  {
    key: 's7', label: '7 · Round',
    bottom: [[0, 1], [0.3, 0.97], [0.55, 0.88], [0.75, 0.7], [0.89, 0.45], [0.97, 0.2], [1, 0.05]],
  },
  {
    key: 's8', label: '8 · Ogee & Bead',
    bottom: [[0, 1], [0.05, 0.85], [0.12, 0.93], [0.28, 0.8], [0.5, 0.52], [0.7, 0.3], [0.82, 0.2], [1, 0.2]],
  },
  {
    key: 's9', label: '9 · Cove & Steps',
    bottom: [[0, 1], [0.2, 0.9], [0.42, 0.7], [0.58, 0.48], [0.68, 0.38], [0.68, 0.28], [0.82, 0.28], [0.82, 0.16], [1, 0.16]],
  },
  {
    key: 's10', label: '10 · Stepped Cyma',
    bottom: [[0, 1], [0.14, 1], [0.14, 0.84], [0.32, 0.78], [0.5, 0.56], [0.66, 0.36], [0.76, 0.3], [0.76, 0.18], [1, 0.18]],
  },
  {
    key: 's11', label: '11 · Round & Shoulder',
    bottom: [[0, 1], [0.12, 1], [0.12, 0.88], [0.38, 0.82], [0.62, 0.66], [0.82, 0.4], [0.94, 0.16], [1, 0.05]],
  },
  {
    key: 's12', label: '12 · Steps',
    bottom: [[0, 1], [0.42, 1], [0.42, 0.62], [0.72, 0.62], [0.72, 0.3], [1, 0.3]],
  },
  {
    key: 's13', label: '13 · Long Taper',
    bottom: [[0, 1], [1, 0.05]],
  },
  {
    key: 's14', label: '14 · Chamfer',
    bottom: [[0, 1], [0.55, 1], [1, 0.08]],
  },
  {
    key: 's15', label: '15 · Eased',
    bottom: [[0, 1], [0.88, 1], [1, 0.84]],
  },
]

/** E4 r3: stored legacy style keys resolve to their nearest chart profile —
 *  models saved under the original three-profile family keep a sensible shape. */
export const LEGACY_PROFILE_ALIASES: Record<string, string> = {
  taper: 's4',
  ogee: 's6',
  plumbSeat: 's12',
  square: 'straight',
}

/**
 * E4: rake OUTLOOKER sizes. E4 r2 (Matt, 2026-08-11, with reference photo):
 * outlookers are DECORATIVE BRACKETS — heavy stock set below the barge rafter,
 * projecting from the rake wall — not the flat hidden members of modern practice.
 * Width is along the rake, depth vertical. Curated like every table here.
 */
export const OUTLOOKER_SIZES: LumberSize[] = [
  { key: '4x4', label: '4×4', widthIn: 3.5, depthIn: 3.5 },
  { key: '4x6', label: '4×6', widthIn: 3.5, depthIn: 5.5 },
  { key: '4x8', label: '4×8', widthIn: 3.5, depthIn: 7.25 },
  { key: '6x6', label: '6×6', widthIn: 5.5, depthIn: 5.5 },
]

export function lumberSize(key: unknown, table: LumberSize[]): LumberSize | null {
  return table.find((s) => s.key === key) ?? null
}

/**
 * THE end-style lookup — tails, outlooker bracket ends, and the barge's lower cut
 * all resolve through here (E4 r3 collapsed the separate outlooker family into
 * this one). Legacy keys alias to their chart equivalent; anything unknown falls
 * back to the Straight Cut default.
 */
export function tailProfile(key: unknown): TailProfile {
  const k = typeof key === 'string' ? (LEGACY_PROFILE_ALIASES[key] ?? key) : key
  return TAIL_PROFILES.find((p) => p.key === k) ?? TAIL_PROFILES[0]
}
